import { useEffect, useState } from 'react';

import { MtxGrid, MtxButton, MtxTooltipAnchor } from '@meilleurtaux/mtp-uikit-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Vector } from '@/../src/assets/icons/Vector';

import { useGetApiAccountProfile, useGetApiAccountKycStatus } from '@/api';
import { GoBackArrowBtn } from '@/components/GoBackArrowBtn/GoBackArrowBtn';
import { Loader } from '@/components/Loader/loader';
import WarningKyc from '@/components/WarningKyc/warningKyc';
import { ROUTE } from '@/constants/route';
import { STATUS_KYC } from '@/enums/statusKYC';
import { MAJOR_ADULT_LEGAL_CAPACITY } from '@/enums/userLegalCapacity';
import { USER_TYPE } from '@/enums/userType';
import { useAuth } from '@/hooks/useAuth';
import { useNavigationToKYC } from '@/hooks/useNavigationToKYC';
import useScreenSize from '@/hooks/useScreenSize';
import { TRACKING_INSTANCE } from '@/hooks/useTracking/useTrackingInit/useTrackingInit';
import ContentModalInformation from '@/modals/ContentModalInformations/ContentModalInformation';
import ContentModalKyc from '@/modals/ContentModalKyc/ContentModalKyc';

import styles from './styles.module.css';

export const Informations: React.FC = () => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { navigateToKYC } = useNavigationToKYC();
  const { data, isLoading, isError } = useGetApiAccountProfile();
  const { data: kycStatus } = useGetApiAccountKycStatus({
    query: {
      enabled: user?.type === USER_TYPE.client,
    },
  });
  const { windowWidth } = useScreenSize();
  const internationalMobileMatch = data?.telephone?.match(/^(\+33)\s?(\d{9})$/);
  const telephone = internationalMobileMatch
    ? `${internationalMobileMatch[1]} ${internationalMobileMatch[2][0]} ${internationalMobileMatch[2].slice(1).replaceAll(/(\d{2})(?=\d)/g, '$1 ')}`
    : data?.telephone?.replaceAll(/(\d{2})(?=\d)/g, '$1 ');

  const [isModalKycOpen, setIsModalKycOpen] = useState<boolean>(false);

  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const [isPhoneModalOpen, setIsPhoneModalOpen] = useState(false);
  const [isAddressModalOpen, setIsAddressModalOpen] = useState(false);

  if (sessionStorage.parcoursVersementData) {
    sessionStorage.removeItem('parcoursVersementData');
  }

  useEffect(() => {
    TRACKING_INSTANCE.setTcVars({
      page_nom: 'mes informations personnelles',
      page_template: 'espace client',
      user_id: user?.id,
      user_client: user?.type === 'Client',
      user_connexion: user?.success,
      user_statut_contrat: sessionStorage.getItem('user_statut_contrat'),
    });
    void TRACKING_INSTANCE.triggerEvent('page', undefined, {
      event_id: 'page',
      event_categorie: 'affichage espace client',
      event_nom: 'mes informations personnelles',
      event_page: 'espace client - mes informations personnelles',
    });
  }, [user]);

  if (isLoading) {
    return <Loader />;
  } else if (isError) {
    navigate(ROUTE.ERREUR, { state: { goTo: ROUTE.DASHBOARD } });
  }

  const infoDetailFirstName = data?.prenom ?? '';
  const infoDetailName = data?.nom ?? '';
  const infoDetailEmail = data?.email ?? '';
  const infoDetailZipCode = data?.codePostal ?? '';
  const infoDetailAddress = data?.adresse ?? '';
  const infoDetailCity = data?.ville ?? '';

  const handleUpdate = () => {
    if (user && user?.capaciteJuridique && user?.capaciteJuridique in MAJOR_ADULT_LEGAL_CAPACITY) {
      void TRACKING_INSTANCE.triggerEvent('clic', undefined, {
        event_id: 'clic',
        event_categorie: 'clic',
        event_nom: 'mettre a jour mes informations',
        event_metier: 'espace client placement',
        event_detail_1: 'cta',
      });
      navigateToKYC();
    } else {
      void TRACKING_INSTANCE.triggerEvent('clic', undefined, {
        event_id: 'clic',
        event_categorie: 'affichage popin données personnelles mineur',
        event_nom: 'affichage popin données personnelles mineur',
        event_metier: 'espace client placement',
        event_detail_1: 'cta',
      });
      setIsModalKycOpen(true);
    }
  };

  return (
    <MtxGrid>
      {isModalKycOpen && (
        <ContentModalKyc
          onClickClose={() => setIsModalKycOpen(false)}
          WarningIcon={kycStatus?.statutKyc === STATUS_KYC.KO}
          legalCapacity={user?.capaciteJuridique}
          variant="secondary"
          location="informations"
        />
      )}

      {kycStatus?.statutKyc === STATUS_KYC.KO && (
        <WarningKyc
          onClickShowModal={() => {
            setIsModalKycOpen(true);
          }}
          legalCapacity={user?.capaciteJuridique}
        />
      )}
      <MtxGrid row>
        <MtxGrid
          className={kycStatus?.statutKyc === STATUS_KYC.KO ? styles.mainTitle : styles.mainTitleNoMarginTop}
          col
          colXs={12}
          colSm={12}
          colMd={12}
          colLg={12}
          colXl={12}
        >
          {windowWidth < 900 && <GoBackArrowBtn />}
          <h1>{t('userInfo.title')}</h1>
        </MtxGrid>
      </MtxGrid>
      <MtxGrid row>
        <MtxGrid className={styles.subTitle} col colXs={12} colSm={12} colMd={12} colLg={12} colXl={12}>
          {windowWidth < 900 && <GoBackArrowBtn />}
          <h2>{t('userInfo.subTitle')}</h2>
        </MtxGrid>
      </MtxGrid>

      <MtxGrid>
        <MtxGrid className={styles.contentCardsInfo} col colXs={12} colSm={12} colMd={12} colLg={12} colXl={12}>
          <div className={styles.informationCard}>
            <div className={styles.informationCardLeftContainer}>
              <div className={styles.logoInfo}>
                <i className="totem-icon totem-icon-mail" />
              </div>
              <div className={styles.info}>
                <span className={styles.infoDetailsName}>
                  <span>{infoDetailFirstName}</span> <span>{infoDetailName}</span>
                </span>
                <span className={styles.infoDetails}>{infoDetailEmail}</span>
              </div>
            </div>
            <MtxButton
              variant="quaternary"
              onClick={() => {
                setIsEmailModalOpen(true);
                void TRACKING_INSTANCE.triggerEvent('clic', undefined, {
                  event_id: 'clic',
                  event_categorie: 'clic',
                  event_nom: 'modifier',
                  event_metier: 'espace client placement',
                  event_detail_1: 'cta',
                  event_detail_2: 'email',
                });
              }}
            >
              <u>{t('common.edit')}</u>
            </MtxButton>
          </div>

          <div className={styles.line}></div>

          <div className={styles.informationCard}>
            <div className={styles.informationCardLeftContainer}>
              <div className={styles.logoInfo}>
                <i className="totem-icon totem-icon-phone" />
              </div>
              <div className={styles.info}>
                <span className={styles.infoDetails}>{telephone}</span>
              </div>
            </div>
            <MtxButton
              variant="quaternary"
              onClick={() => {
                setIsPhoneModalOpen(true);
                void TRACKING_INSTANCE.triggerEvent('clic', undefined, {
                  event_id: 'clic',
                  event_categorie: 'clic',
                  event_nom: 'modifier',
                  event_metier: 'espace client placement',
                  event_detail_1: 'cta',
                  event_detail_2: 'telephone',
                });
              }}
            >
              <u>{t('common.edit')}</u>
            </MtxButton>
          </div>

          <div className={styles.line}></div>

          <div className={styles.informationCard}>
            <div className={styles.informationCardLeftContainer}>
              <div className={styles.logoInfo}>
                <i className="totem-icon totem-icon-location" />
              </div>
              <div className={styles.info}>
                <span className={styles.infoDetails}>{infoDetailAddress}</span>
                <span className={styles.infoDetails}>
                  {infoDetailZipCode} {infoDetailCity}
                </span>
              </div>
            </div>
            <MtxButton
              variant="quaternary"
              onClick={() => {
                setIsAddressModalOpen(true);
                void TRACKING_INSTANCE.triggerEvent('clic', undefined, {
                  event_id: 'clic',
                  event_categorie: 'clic',
                  event_nom: 'modifier',
                  event_metier: 'espace client placement',
                  event_detail_1: 'cta',
                  event_detail_2: 'adresse postale',
                });
              }}
            >
              <u>{t('common.edit')}</u>
            </MtxButton>
          </div>
        </MtxGrid>
      </MtxGrid>
      {user?.type !== USER_TYPE.prospect && (
        <MtxGrid>
          <MtxGrid col colXs={12} colSm={12} colMd={12} colLg={12} colXl={12}>
            {windowWidth < 900 && <GoBackArrowBtn />}
            <div className={styles.iconSubTitle}>
              <h2>{t('userInfo.personalDataSubtitle')}</h2>
              <MtxTooltipAnchor
                className={styles.tooltipAnchor}
                placement={windowWidth < 900 ? 'left-end' : 'right-end'}
                offset={20}
                visible={false}
                trigger="hover"
                autoadjust={true}
              >
                <i className="totem-icon totem-icon-info-circle" />
                <div className={styles.tooltipContent} slot="content">
                  {windowWidth > 899 && (
                    <Vector className={styles.vectorIcon} height={24} width={24} color="var(--totem-color-white)" />
                  )}
                  <span className={styles.tooltipTitle}>{t('userInfo.tooltipTitle')}</span>
                  <span className={styles.tooltipText}>{t('userInfo.tooltipText')}</span>
                </div>
              </MtxTooltipAnchor>
            </div>
            <div>{t('userInfo.description')}</div>
          </MtxGrid>
          <MtxGrid className={styles.contentCardsInfo} colXs={12} colSm={12} colMd={12} colLg={12} colXl={12}>
            <div className={styles.informationCard}>
              <div className={styles.informationCardLeftContainer}>
                <div className={styles.logoInfo}>
                  <i className="totem-icon totem-icon-user" />
                </div>

                <div className={styles.info}>
                  <span className={styles.infoDetails}>{t('userInfo.subTitleKYC')}</span>
                </div>
              </div>
              <div>
                <MtxButton variant="quaternary" onClick={handleUpdate}>
                  <u>{t('common.update')}</u>
                </MtxButton>
              </div>
            </div>
          </MtxGrid>
        </MtxGrid>
      )}
      <ContentModalInformation
        isEmailModalOpen={isEmailModalOpen}
        isPhoneModalOpen={isPhoneModalOpen}
        isAddressModalOpen={isAddressModalOpen}
        setIsEmailModalOpen={setIsEmailModalOpen}
        setIsPhoneModalOpen={setIsPhoneModalOpen}
        setIsAddressModalOpen={setIsAddressModalOpen}
      />
    </MtxGrid>
  );
};
